import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormLabel,
  Icon,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { FaDownload, FaListUl, FaSyncAlt } from 'react-icons/fa';
import PropTypes from 'prop-types';
import CommonTable from '../../../components/commonTable/CommonTable';
import CommonPagination from '../../../components/commonPagination/CommonPagination';
import InvoiceJobResultPanel from '../../../components/invoiceJobs/InvoiceJobResultPanel';
import {
  GetInvoiceJobs,
  DownloadInvoiceJobFile,
} from 'reactRedux/thunks/settlementsThunk/SettlementsThunk';

// Auto-refresh cadence while the Invoice Jobs tab is open (RAT-1810).
const JOB_POLL_INTERVAL_MS = 5000;

// Statuses that can still change on their own, so the visible page is worth
// re-fetching. Once no row on the current page is active, polling stops
// (RAT-1827).
const ACTIVE_JOB_STATUSES = ['pending', 'in_progress'];

const OPERATION_LABEL = {
  merchant_download: 'Download',
  merchant_email: 'Email',
  merchant_generate: 'Generate',
  // RAT-1826: preview (PV-) invoice download - not an issued invoice.
  merchant_preview_download: 'Preview',
};

const COMPLETED_LABEL = {
  merchant_email: 'Sent',
  merchant_generate: 'Generated',
};

// A finished run always has a breakdown worth reading; a queued one has nothing
// to show yet.
const RESULT_JOB_STATUSES = ['completed', 'failed'];

// Maps a job to its status chip: label + Chakra colorScheme + optional tooltip.
const getStatusDisplay = (job) => {
  switch (job?.status) {
    case 'pending':
      return { label: 'Queued', colorScheme: 'gray' };
    case 'in_progress':
      return {
        label: `Processing ${job?.done ?? 0}/${job?.total ?? 0}`,
        colorScheme: 'orange',
      };
    case 'completed': {
      // RAT-1914: a run that held part of the batch back is completed, not
      // failed - but it must not read as if everything went out either.
      const done = COMPLETED_LABEL[job?.operation] || 'Ready';
      const skipped = job?.skippedCount ?? 0;
      const warnings = job?.warningCount ?? 0;
      const errors = job?.errorCount ?? 0;
      const failedDeliveries = job?.failedDeliveryCount ?? 0;

      // A bounce means the merchant did not get their invoice, so the run is a
      // failure regardless of the job having completed. Checked first: the job
      // result is written the moment SES accepts the message and then frozen,
      // so on its own it will always read "Sent" - which is precisely the false
      // success this reporting exists to remove.
      //
      // Only a permanent bounce or a spam complaint counts; a transient bounce
      // is excluded upstream, because turning a completed run red for a full
      // mailbox that will clear on its own makes the status untrustworthy.
      if (failedDeliveries > 0) {
        const delivered = Math.max((job?.done ?? 0) - failedDeliveries, 0);
        return {
          label:
            delivered === 0
              ? 'Failed - not delivered'
              : `${failedDeliveries} of ${job?.total ?? 0} failed`,
          colorScheme: 'red',
          tooltip:
            'The email was accepted for sending but never reached the merchant - it bounced or was reported as spam. Open Results, or the merchant page, for the address and the reason.',
        };
      }

      // RAT-1714: a completed run that failed for some of its merchants used to
      // read as a plain green "Sent". The Results panel had the failures all
      // along, but nobody opens Results on a row that looks fine - so a run
      // where nothing was delivered was indistinguishable from one where
      // everything was. Counts rather than a suffix, because "Sent, 1 failed"
      // still leads with the word Sent when nothing was.
      //
      // Checked before skipped and warnings: a failure outranks both, and
      // Results carries the full breakdown when a run has more than one kind.
      if (errors > 0) {
        return {
          label: `${job?.done ?? 0} of ${job?.total ?? 0} ${done.toLowerCase()}`,
          colorScheme: (job?.done ?? 0) === 0 ? 'red' : 'orange',
          tooltip:
            (job?.done ?? 0) === 0
              ? 'Nothing went out. Open Results for the reason against each merchant.'
              : 'Some merchants failed. Open Results for the reason against each one.',
        };
      }

      if (skipped > 0) {
        return {
          label: `${done}, ${skipped} skipped`,
          colorScheme: 'yellow',
          tooltip:
            'Some tax numbers were not confirmed by the registry, so those invoices were held back. Open Results for the reasons.',
        };
      }
      if (warnings > 0) {
        return {
          label: `${done}, ${warnings} warning${warnings > 1 ? 's' : ''}`,
          colorScheme: 'orange',
          tooltip:
            'Issued, with something the tax registry flagged. Open Results for the details.',
        };
      }
      return { label: done, colorScheme: 'green' };
    }
    case 'failed':
      return {
        label: 'Failed',
        colorScheme: 'red',
        tooltip: job?.firstError || 'Job failed',
      };
    default:
      return { label: job?.status || 'Unknown', colorScheme: 'gray' };
  }
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const InvoiceJobsList = ({ onGenerateClick, refreshKey }) => {
  const dispatch = useDispatch();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const { invoiceJobs, invoiceJobsTotal, invoiceJobsLoading } = useSelector(
    (state) => state.SettlementsSlice,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [mineOnly, setMineOnly] = useState(false);
  // RAT-1914: which job's result panel is open, if any.
  const [openJob, setOpenJob] = useState(null);

  const fetchJobs = useCallback(
    (silent = false) => {
      dispatch(GetInvoiceJobs({ page, limit, mine: mineOnly }, silent));
    },
    [dispatch, page, limit, mineOnly],
  );

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Only the rows currently on screen decide whether polling is needed, so
  // switching pages re-evaluates it: a page holding a queued/processing job
  // starts the background refresh, a page of finished jobs runs no timer.
  const hasActiveJobs = (invoiceJobs || []).some((job) =>
    ACTIVE_JOB_STATUSES.includes(job?.status),
  );

  // Background poll: refreshes the rows in place (no loader) and clears itself
  // as soon as the last active job on this page reaches a final status. The
  // component only mounts while the Invoice Jobs tab is active, so polling also
  // stops the moment the user leaves the tab.
  useEffect(() => {
    if (!hasActiveJobs) return undefined;
    const timer = setInterval(() => fetchJobs(true), JOB_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActiveJobs, fetchJobs]);

  // A job enqueued while this tab is already open would otherwise stay hidden
  // until the next manual refresh, since polling is off when nothing is active.
  // The new job sits at the top of the list, so go back to the first page.
  const lastRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKeyRef.current === refreshKey) return;
    lastRefreshKeyRef.current = refreshKey;
    if (page === 1) {
      fetchJobs(true);
    } else {
      setPage(1);
    }
  }, [refreshKey, page, fetchJobs]);

  const handleDownload = useCallback(
    (job) => {
      const isZip = (job?.merchantCount ?? 0) > 1;
      dispatch(
        DownloadInvoiceJobFile(
          job.queueId,
          `invoice-job-${job.queueId}`,
          isZip,
        ),
      );
    },
    [dispatch],
  );

  const columnHelper = createColumnHelper();
  const headerText = (label) => (
    <Text
      align="left"
      fontSize={{ sm: '10px', lg: '12px' }}
      color="gray.400"
      whiteSpace="nowrap"
    >
      {label}
    </Text>
  );
  const cellText = (value) => (
    <Text
      color={textColor}
      fontSize="sm"
      fontWeight="600"
      align="left"
      whiteSpace="nowrap"
    >
      {value}
    </Text>
  );

  // Plain array (not memoized) to match the MerchantList columns pattern.
  const columns = [
    columnHelper.accessor((row) => row?.periodLabel, {
      id: 'periodLabel',
      header: () => headerText('Period'),
      cell: (info) => cellText(info.getValue() || 'N/A'),
    }),
    columnHelper.accessor((row) => row?.merchantLabel, {
      id: 'merchant',
      header: () => headerText('Merchant(s)'),
      cell: (info) =>
        cellText(info.getValue() || `${info.row.original?.merchantCount ?? 0}`),
    }),
    columnHelper.accessor((row) => row?.operation, {
      id: 'operation',
      header: () => headerText('Type'),
      cell: (info) => cellText(OPERATION_LABEL[info.getValue()] || 'N/A'),
    }),
    columnHelper.accessor((row) => row?.requestedByName, {
      id: 'requestedByName',
      header: () => headerText('Requested by'),
      cell: (info) => cellText(info.getValue() || 'N/A'),
    }),
    columnHelper.accessor((row) => row?.status, {
      id: 'status',
      header: () => headerText('Status'),
      cell: (info) => {
        const display = getStatusDisplay(info.row.original);
        const badge = (
          <Badge
            colorScheme={display.colorScheme}
            borderRadius="6px"
            px="8px"
            py="2px"
            fontSize="xs"
            textTransform="none"
          >
            {display.label}
          </Badge>
        );
        return display.tooltip ? (
          <Tooltip label={display.tooltip} hasArrow>
            {badge}
          </Tooltip>
        ) : (
          badge
        );
      },
    }),
    columnHelper.accessor((row) => row?.createdAt, {
      id: 'createdAt',
      header: () => headerText('Created'),
      cell: (info) => cellText(formatDateTime(info.getValue())),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => headerText('Action'),
      cell: (info) => {
        const job = info.row.original;
        const canShowResult = RESULT_JOB_STATUSES.includes(job?.status);
        if (!job?.downloadable && !canShowResult) {
          return (
            <Text color="gray.400" fontSize="sm">
              -
            </Text>
          );
        }
        // RAT-1714: Download and Results are two different kinds of thing - one
        // takes a file, the other explains what happened - and side by side as
        // bare ghost buttons they read as one run-on control. A divider between
        // them and room to breathe; `nowrap` because "Download" wrapping to two
        // lines was making the row taller than every other row.
        const hasBoth = job?.downloadable && canShowResult;
        return (
          <Flex align="center" gap={hasBoth ? 2 : 1} flexWrap="nowrap">
            {job?.downloadable && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="blue"
                leftIcon={<Icon as={FaDownload} w="12px" h="12px" />}
                onClick={() => handleDownload(job)}
                className="no-navigate"
                whiteSpace="nowrap"
                flexShrink={0}
                px={2}
              >
                Download
              </Button>
            )}
            {hasBoth && (
              <Divider
                orientation="vertical"
                h="18px"
                borderColor="gray.300"
                flexShrink={0}
              />
            )}
            {canShowResult && (
              <Button
                size="sm"
                variant="ghost"
                // RAT-1714: a failure draws the eye harder than a hold-back.
                // Results was grey on a run where nothing was delivered, which
                // is the row most worth opening.
                colorScheme={
                  (job?.errorCount ?? 0) > 0
                    ? 'red'
                    : (job?.skippedCount ?? 0) > 0
                      ? 'yellow'
                      : 'gray'
                }
                leftIcon={<Icon as={FaListUl} w="12px" h="12px" />}
                onClick={() => setOpenJob(job)}
                className="no-navigate"
                whiteSpace="nowrap"
                flexShrink={0}
                px={2}
              >
                Results
              </Button>
            )}
          </Flex>
        );
      },
    }),
  ];

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={3}
        mb={3}
        mt={2}
      >
        <Flex align="center" gap={2}>
          <Text fontSize="lg" fontWeight="700" color={textColor}>
            Invoice Jobs
          </Text>
          {invoiceJobsLoading && <Spinner size="sm" color="orange.400" />}
        </Flex>
        <Flex align="center" gap={4}>
          <Flex align="center" gap={2}>
            <FormLabel htmlFor="mine-only" mb="0" fontSize="sm">
              My jobs only
            </FormLabel>
            <Switch
              id="mine-only"
              isChecked={mineOnly}
              onChange={(e) => {
                setPage(1);
                setMineOnly(e.target.checked);
              }}
              colorScheme="orange"
            />
          </Flex>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FaSyncAlt} w="12px" h="12px" />}
            onClick={() => fetchJobs()}
          >
            Refresh
          </Button>
          {typeof onGenerateClick === 'function' && (
            <Button
              size="sm"
              bg="primary"
              color="white"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              onClick={onGenerateClick}
            >
              Generate Invoices
            </Button>
          )}
        </Flex>
      </Flex>

      {openJob && (
        <InvoiceJobResultPanel job={openJob} onClose={() => setOpenJob(null)} />
      )}

      <CommonTable
        columns={columns}
        data={invoiceJobs || []}
        isLoading={invoiceJobsLoading}
        keyValue="queueId"
      />

      <CommonPagination
        totalCount={invoiceJobsTotal}
        activePage={page}
        pageCount={Math.ceil((invoiceJobsTotal || 0) / limit)}
        onPageChange={setPage}
        itemsPerPage={limit}
        onItemsPerPageChange={(newLimit) => {
          setPage(1);
          setLimit(newLimit);
        }}
      />
    </Box>
  );
};

InvoiceJobsList.propTypes = {
  onGenerateClick: PropTypes.func,
  refreshKey: PropTypes.number,
};

export default InvoiceJobsList;
